import { action, makeObservable, computed, observable } from 'mobx';
import { Store } from '../../store/store';
import { BindingProps, Message, StoreNode } from '../../store';
import {
  InputState,
  input,
  inputGroup,
} from '../../components/input';
import { ApiVariables } from '../../api';
import { notifyError, notifySuccess } from '../../services/notifications';
import { GenerativeAiProvider } from '@clipr/lib';
import { ToggleCliprGptWindowState } from '../teamSettings/toggleCliprGptWindowState';

type GenerativeAiFormStateProps = BindingProps<{
  teamId?: string;
  refreshTeam?: (() => void) | null;
}>;

export class GenerativeAiFormState
  extends StoreNode {

  constructor(store: Store, props?: GenerativeAiFormStateProps) {
    super(store, props);
    makeObservable<this, 'handleSubmitReject' | 'handleSubmitSuccess'>(this, {
      generativeAiProvider: observable,
      teamId: computed,
      team: computed,
      init: action,
      changeAiProvider: action,
      toggleSummarization: action,
      toggleCliprGpt: action,
      toggleActionItems: action,
      disableCliprGpt: action,
      disableSummarization: action,
      disableCliprGptExtraActions: action,
      toggleSendVideoProcessedNotificationToAllMembers: action,
      handleSubmitReject: action,
      handleSubmitSuccess: action,
    });

    this.cliprGptEmailNotifications = input(this, {
      name: 'cliprGptEmailNotifications'
    });
  }

  generativeAiProvider: GenerativeAiProvider | null = null;

  get teamId(): string {
    return this.resolvedProps.teamId;
  }

  get team() {
    return this.store.teamManager.getTeam(this.teamId);
  }

  readonly cliprGptEmailNotifications: InputState;

  readonly form = inputGroup(this, {
    name: 'generativeAiForm',
    inputs: () => {
      return [
        this.cliprGptEmailNotifications
      ]
    }
  });

  init() {
    if (!this.team) return;
    this.cliprGptEmailNotifications.loadValue(!!this.team.isSendVideoProcessedNotificationToAllMembers);
    this.generativeAiProvider = this.team.generativeAiProvider;
  }

  async changeAiProvider(value: string) {
    if (!this.team) return;

    const window = this.store.toggleCliprGptWindow;
    const listener = (msg: Message<ToggleCliprGptWindowState>) => {
      switch (msg.type) {
        case 'success':
          this.props.refreshTeam?.();
          break;
        case 'close':
          window.unlisten(listener);
          break;
      }
    }
    window.listen(listener);
    window.open({
      teamId: this.team?.id,
      provider: value as GenerativeAiProvider,
      summarizationEnabled: this.team.summarizationEnabled,
      actionItemsExtractionEnabled: this.team.actionItemsExtractionEnabled,
      cliprGptEnabled: this.team.cliprGptEnabled,
      header: 'Change AI Provider',
      button: 'Confirm',
      description: 'Are you sure you want to change the AI provider? This means that all the videos from this library will be reprocessed.'
    });
  }

  async toggleSummarization() {
    if (!this.team) return;

    if (this.team?.summarizationEnabled) {
      await this.disableSummarization();
      return;
    }

    const window = this.store.toggleCliprGptWindow;
    const listener = (msg: Message<ToggleCliprGptWindowState>) => {
      switch (msg.type) {
        case 'success':
          this.props.refreshTeam?.();
          break;
        case 'close':
          window.unlisten(listener);
          break;
      }
    }
    window.listen(listener);
    window.open({
      teamId: this.team?.id,
      provider: this.generativeAiProvider as GenerativeAiProvider ?? this.team.generativeAiProvider,
      summarizationEnabled: !this.team.summarizationEnabled,
      actionItemsExtractionEnabled: this.team.actionItemsExtractionEnabled,
      cliprGptEnabled: this.team.cliprGptEnabled,
      header: 'Enable video summary',
      description: 'By enabling AI video summary, we will send parts of the video transcript to an AI provider to generate a summary, for all the existing and the new videos from this library.'
    });
  }

  async toggleCliprGpt() {
    if (!this.team) return;

    if (this.team?.cliprGptEnabled) {
      await this.disableCliprGpt();
      return;
    }

    const window = this.store.toggleCliprGptWindow;
    const listener = (msg: Message<ToggleCliprGptWindowState>) => {
      switch (msg.type) {
        case 'success':
          this.props.refreshTeam?.();
          break;
        case 'close':
          window.unlisten(listener);
          break;
      }
    }
    window.listen(listener);
    window.open({
      teamId: this.team?.id,
      provider: this.generativeAiProvider as GenerativeAiProvider ?? this.team.generativeAiProvider,
      summarizationEnabled: this.team.summarizationEnabled,
      actionItemsExtractionEnabled: this.team.actionItemsExtractionEnabled,
      cliprGptEnabled: !this.team.cliprGptEnabled,
      header: 'Enable CLIPr GPT',
      description: 'By enabling CLIPr GPT, we will send parts of the video transcript to the AI provider to generate answers, for all the existing and the new videos from this library.'
    });
  }

  async toggleActionItems() {
    if (!this.team) return;

    if (this.team?.actionItemsExtractionEnabled) {
      await this.disableCliprGptExtraActions();
      return;
    }

    const window = this.store.toggleCliprGptWindow;
    const listener = (msg: Message<ToggleCliprGptWindowState>) => {
      switch (msg.type) {
        case 'success':
          this.props.refreshTeam?.();
          break;
        case 'close':
          window.unlisten(listener);
          break;
      }
    }
    window.listen(listener);
    window.open({
      teamId: this.team?.id,
      provider: this.generativeAiProvider as GenerativeAiProvider ?? this.team.generativeAiProvider,
      summarizationEnabled: this.team.summarizationEnabled,
      actionItemsExtractionEnabled: !this.team.actionItemsExtractionEnabled,
      cliprGptEnabled: this.team.cliprGptEnabled,
      header: 'Enable action items',
      description: 'By enabling AI action items, we will send parts of the video transcript to an AI provider to generate action items and next steps, for all the new videos from this library.'
    });
  }

  async disableCliprGpt() {
    const vars: ApiVariables<'updateTeam'> = {
      args: {
        teamId: this.team?.id || '',
        generativeAiSettings: {
          summarizationEnabled: this.team?.summarizationEnabled ?? false,
          actionItemsExtractionEnabled: this.team?.actionItemsExtractionEnabled ?? false,
          cliprGptEnabled: false,
          provider: this.team?.generativeAiProvider ?? GenerativeAiProvider.Amazon
        }
      }
    };

    const [, err] = await this.store.teamManager.updateTeam(vars);
    if (err) {
      this.handleSubmitReject(`Could not update team. Please try again.`);
      return;
    }

    this.handleSubmitSuccess(`Team updated.`);
    this.props.refreshTeam?.();
  }

  async disableSummarization() {
    const vars: ApiVariables<'updateTeam'> = {
      args: {
        teamId: this.team?.id || '',
        generativeAiSettings: {
          summarizationEnabled: false,
          actionItemsExtractionEnabled: this.team?.actionItemsExtractionEnabled ?? false,
          cliprGptEnabled: this.team?.cliprGptEnabled ?? false,
          provider: this.team?.generativeAiProvider ?? GenerativeAiProvider.Amazon
        }
      }
    };

    const [, err] = await this.store.teamManager.updateTeam(vars);
    if (err) {
      this.handleSubmitReject(`Could not update team. Please try again.`);
      return;
    }

    this.handleSubmitSuccess(`Team updated.`);
    this.props.refreshTeam?.();
  }

  async disableCliprGptExtraActions() {
    const vars: ApiVariables<'updateTeam'> = {
      args: {
        teamId: this.team?.id || '',
        generativeAiSettings: {
          summarizationEnabled: this.team?.summarizationEnabled ?? false,
          actionItemsExtractionEnabled: false,
          cliprGptEnabled: this.team?.cliprGptEnabled ?? false,
          provider: this.team?.generativeAiProvider ?? GenerativeAiProvider.Amazon
        }
      }
    };

    const [, err] = await this.store.teamManager.updateTeam(vars);
    if (err) {
      this.handleSubmitReject(`Could not update library. Please try again.`);
      return;
    }

    this.handleSubmitSuccess(`Library updated.`);
    this.props.refreshTeam?.();
  }

  async toggleSendVideoProcessedNotificationToAllMembers() {
    const vars: ApiVariables<'updateTeam'> = {
      args: {
        teamId: this.team?.id || '',
        sendVideoProcessedNotificationToAllMembers: !this.team?.sendVideoProcessedNotificationToAllMembers
      }
    };

    const [, err] = await this.store.teamManager.updateTeam(vars);
    if (err) {
      this.handleSubmitReject(`Could not update library. Please try again.`);
      return;
    }

    this.handleSubmitSuccess(`Library updated.`);
    this.props.refreshTeam?.();
  }

  reset() {
    this.form.clear();
  }

  export(): unknown {
    return this.form.export();
  }

  private handleSubmitReject(msg: string) {
    this.form.handleSubmitReject();
    notifyError(this, msg);
  }

  private handleSubmitSuccess(msg: string) {
    this.props.refreshTeam?.();
    this.init();
    this.form.handleSubmitResolve();
    notifySuccess(this, msg);
  }
}
